import { createApp, type App } from "vue";
import { createPinia } from "pinia";
import { createVuetify } from "vuetify";
import { initializeJQueryHandlerTypes } from "@/JQueryHandlerTypes";
import { setup } from "@/axios";
import { MiaPlaza } from "@/Reinforced.Typings";
import { enableLegacyControls } from "@/LegacyControls";
import SignalRConnection from "@/utils/SignalRConnection";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

// Vue modules that are not actually Vue components.
import I18Next from "@/Control/Vue/I18Next";
import Markdown from "@/Markdown/Markdown";
import { SnotifyConfig } from "@/Control/Vue/Notifications/Shared/SnotifyConfig";
import { useI18NextStore } from "@/Control/Vue/Stores/I18NextStore";

// Vue external components or packages that can be injected using app.use(*);
// Please keep this list sorted to avoid merge conflicts.
import Snotify from "vue3-snotify";
import timeago from "vue-timeago3";

// Vue components that can be injected from the backend with mia-* tags.
import { registerMiaComponents } from "@/MiaComponentRegistry";

import Log from "./utils/Log";
import Notifier from "@/Control/Vue/Notifications/Shared/Notifier";
import { setUserTimeZone } from "@/utils/UserTimeZone";
import { registerFundsUpdateHandler } from "@/SignalR/FundsUpdates";
import { registerVitePreloadErrorHandler } from "@/utils/VitePreloadErrorHandler";

// These imports are only markers for the chunking so that these modules are always placed into the shared entrypoint chunk.
// Please keep this list sorted to avoid merge conflicts.
import "@/AppContainer.vue";
import "@/chunks/Pdf";
import "@/common/CheckBox.vue";
import "@/common/FileDownloader";
import "@/common/PromiseLoader.vue";
import "@/common/RadioButton.vue";
import "@/Control/Vue/Defaults";
import "@/Control/Vue/Lesson/Lightbox.vue";
import "@/Control/Vue/Lists/Searchable/SearchableList.vue";
import "@/Control/Vue/Lists/VueList/Items/Item";
import "@/Control/Vue/Lists/VueList/VueListUrlHelper";
import "@/Control/Vue/Router";
import "@/Control/Vue/SearchInput.vue";
import "@/Control/Vue/VimeoPlayer.vue";
import "@/Control/Vue/VimeoPlayerWithFallback.vue";
import "@/Control/Vue/VimeoS3FallbackPlayer.vue";
import "@/curriculum/common/Expandable.vue";
import "@/utils/NonReactiveProps";
import "@/utils/SubjectHelper";
import "@/Vue.PropTypes";
import "@miaplaza/mia-colors";
import "@theme/cross_outlined.svg";
import "@theme/cross.svg";
import "@theme/curriculum/icons/custom_break_icon.svg";
import "@theme/eye-slash.svg";
import "@theme/eye.svg";
import "@theme/icons/arrow-left.svg";
import "@theme/icons/arrow-right.svg";
import "@theme/icons/arrow-square-out.svg";
import "@theme/icons/caret_down_icon.svg";
import "@theme/icons/close_icon.svg";
import "@theme/icons/download_icon.svg";
import "@theme/icons/edit_icon.svg";
import "@theme/icons/info_icon_filled.svg";
import "@theme/icons/lock-outline.svg";
import "@theme/icons/lock.svg";
import "@theme/icons/plus_no_circle_icon.svg";
import "@theme/icons/video_icon.svg";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import "vue-inline-svg";
import "vue-select";
import "vue-select/dist/vue-select.css";
import "vue-tippy";
import "vue3-text-clamp";

// Axios Configuration
setup();

// Create a shared Pinia instance to maintain a unified state across multiple Vue applications.
// This ensures that legacy Vue components, which are instantiated separately from the main Vue app,
// can still access and share the same global store.
const pinia = createPinia();

// Set up global error handlers
window.onerror = Log.onWindowError;
window.onunhandledrejection = Log.onUnhandledRejection;
registerVitePreloadErrorHandler();

// Practice and Assessment is using this plugin to persist the state in the browser's local storage.
pinia.use(piniaPluginPersistedstate);

/**
 * Creates a new Vue app instance (runs `Vue.createApp()`) with all necessary plugins and configurations.
 * It also registers all mia-* components that are injected by the backend code using the `registerMiaComponents` function.
 *
 * @remarks
 * This function doesn't mount the app, it only creates it. We separated the creation from the mounting
 * because in legacy code, we sometimes need to mount components individually. In Vue 3, we can't mount
 * components in isolation as they might have dependencies on plugins or other configurations that need to be
 * set up first such as Pinia or I18Next.
 *
 * @param name - The name of the Vue application.
 * @param appSettings - Optional settings for the Vue application.
 */
export function createVueApplication(name: string, appSettings?: Record<string, unknown>): App<Element> {
	const app = createApp({
		name,
		// We replace Vue's default delimiters {{ }} with random strings. We do not want to use
		// interpolation in the template that is rendered by the web server but only in the template
		// blocks of our components. Note that we already set v-pre on many controls on the web server
		// which disables interpolation but we can not always set this (e.g. not on literals.)
		delimiters: [Math.random().toString(36), Math.random().toString(36)],
		...(appSettings ?? {}),
	});

	app.use(pinia);

	app.use(Snotify, SnotifyConfig);

	// vue-timeago3 needs to be initialized globally (https://github.com/0x0dr1y/vue-timeago3/issues/640, though would be very easy to fix upstream.)
	app.use(timeago);

	// it seems necessary to globally initialize vuetify; though not definitely clear why.
	app.use(createVuetify());

	// Enable TextResources™
	app.use(I18Next.install);
	app.use(Markdown.install);

	// Vue components that can be injected by the backend code as mia-* tags.
	// It does `app.component("mia-*", *);` for all injected components.
	registerMiaComponents(app);

	return app;
}

/**
 * Mounts the main Vue application for the website.
 * This is the primary entry point that initializes and mounts our Vue application
 * to the DOM, setting up all necessary configurations, plugins, and global components.
 *
 * @remarks
 * This function is called in /${SITE}/${THEME}/entrypoint.ts
 */
export function mountMainVueApplication(): void {
	const app = createVueApplication("miaplaza-website", {
		mounted: () => {
			// Undo the effects of developer.css once the Vue app and all our CSS is ready.
			// This only has an effect on developer builds, see developer.css for details.
			// On production builds display is unset anyway.
			document.body.style.cssText = "display: unset;";

			initializeJQueryHandlerTypes();
			void enableLegacyControls();

			// This is used by the legacy code to create a new Vue application.
			window.createVueApplication = createVueApplication;

			// Allow content developers to see unreplaced text resource keys by typing MiaCulture() into the console.
			window.MiaCulture = (culture?: string) => {
				const store = useI18NextStore();
				store.setCulture(culture);
				return store.culture;
			};

			// Track active member sessions and handles "UpdateFunds" via SignalR
			void SignalRConnection.connect(MiaPlaza.SignalR.SessionHub.PATH, (connection) => {
				connection.on("ShowNotification", (notification: MiaPlaza.Control.Vue.Notifications.Shared.INotification) => {
					Notifier.show(notification);
				});
				connection.on("RequestTimezoneUpdate", () => {
					void connection.invoke("UpdateTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
				});
				connection.on("SetTimezone", (timeZoneId: string) => {
					setUserTimeZone(timeZoneId);
				});
				registerFundsUpdateHandler(connection);
			});
		},
	});

	// Vue 3 global error handler
	app.config.errorHandler = Log.onVueError;

	// We make the app instance accessible to the components (used to
	// late-initialize the vue-router in Root components.)
	app.provide("app", app);

	app.mount("#app");
}
